import React, { useEffect, useState } from "react";
import Layout from "../layouts/Layout";
import { useNavigate } from "react-router-dom";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { frFR } from "@mui/x-data-grid/locales";
import StatusBadge from "../components/ui/StatusBadge";
import { convertAmountToPretty, prettyIdProcurement, strToFormattedDate } from "../helpers/formatHelper";
import { getColorByStep, Step } from "../helpers/stepHelper";
import { getProcurements } from "../services/api/procurementService";
import { toast } from "react-toastify";
import { useUserContext } from "../context/userContext";
import { enforcePolicy } from "../services/api/userService";
import Switch from '@mui/material/Switch';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import { Button } from "@mui/material";
import LightbulbIcon from '@mui/icons-material/Lightbulb';

export default function Home() {
  const { user } = useUserContext();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const [procurements, setProcurements] = useState([]);
  const [hasCreatePermission, setHasCreatePermission] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState({ initiator: false });
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [showTerminated, setShowTerminated] = useState(false);
  const [selectedInitiator, setSelectedInitiator] = useState("");
  const [initiatorsList, setInitiatorsList] = useState([]);

  const TERMINATED_STEPS_NAME = [Step.END, Step.UNASSIGNED];

  function sortProcurements(a, b) {
    const isBlueA = a.isUserAction;
    const isBlueB = b.isUserAction;
    const isEndA = TERMINATED_STEPS_NAME.some(stepName => a.step?.name === stepName);
    const isEndB = TERMINATED_STEPS_NAME.some(stepName => b.step?.name === stepName);
    if (isBlueA && !isBlueB) return -1;
    if (!isBlueA && isBlueB) return 1;
    if (isEndA && !isEndB) return 1;
    if (!isEndA && isEndB) return -1;
    return String(a.id).localeCompare(String(b.id), undefined, { numeric: true });
  }

  const columns = [
    {
      field: "id",
      headerName: "Numéro",
      renderCell: params => <span>{prettyIdProcurement(params.value)}</span>,
    },
    {
      field: "name",
      headerName: "Nom du marché",
      flex: 2,
      renderCell: params => (
        <span
          className="flex w-full text-sky-500 cursor-pointer"
          onMouseDown={event => handleClick(event, params.row.uuid)}
        >
          <span className="mr-1">{params.row.isUserAction ? "⏳" : ""}</span>
          <span className="underline decoration-sky-500">{params.value}</span>
        </span>
      ),
    },
    {
      field: "initiator",
      headerName: "Initiateur",
      flex: 1,
      sortable: true,
      sortComparator: (a, b) => {
        const nameA = a?.fullname || a || "";
        const nameB = b?.fullname || b || "";
        return nameA.localeCompare(nameB, "fr", { sensitivity: "base" });
      },
      renderCell: params => (
        <span>{params.row?.initiator?.fullname || params.row?.initiator || "-"}</span>
      ),
    },
    {
      field: "amountExcludingVAT",
      headerName: "Montant HTVA",
      type: "number",
      valueFormatter: value => convertAmountToPretty(value),
      headerAlign: "left",
      flex: 0.6,
      renderCell: params => (
        <div style={{ textAlign: "left", width: "100%" }}>
          {convertAmountToPretty(params.value)}
        </div>
      ),
    },
    {
      field: "fundingSource",
      headerName: "Financement",
      flex: 0.7,
    },
    {
      field: "step",
      headerName: "Statut",
      flex: 3,
      sortComparator: (a, b) => {
        if (!a && !b) return 0;
        if (!a) return 1;
        if (!b) return -1;
        return a.name.localeCompare(b.name);
      },
      renderCell: params => (
        <div className="flex items-center h-full">
          <StatusBadge
            text={params.value.status}
            color={getColorByStep[params.value.name]}
            size="small"
          />
        </div>
      ),
    },
    {
      field: "submittedAt",
      type: "Date",
      headerName: "Date de la demande",
      valueFormatter: value => strToFormattedDate(value),
      flex: 0.7,
    },
    {
      field: "isUserAction",
      type: "boolean",
      headerName: "Action requise",
    },
  ];

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const mediaChange = (event) => {
      setColumnVisibility(event.matches
        ? { ...columnVisibility, isUserAction: false, initiator: false }
        : { ...columnVisibility, isUserAction: false, fundingSource: false, step: false, formSubmittedAt: false, initiator: false },
      );
    };
    mediaChange(mq);
    mq.addEventListener("change", mediaChange);
    return () => {
      mq.removeEventListener("change", mediaChange);
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    async function fetch() {
      try {
        setIsLoading(true);
        const result = await getProcurements();
        setProcurements(Array.isArray(result) ? result : []);
        const canCreateProcurementResponse = await enforcePolicy(user.uuid, "/procurement", "POST");
        setHasCreatePermission(canCreateProcurementResponse);

        const uniqueInitiators = [];
        const seenIds = new Set();
        Array.isArray(result) && result.forEach((p) => {
          const id = p.initiator?.uuid || p.initiator || "";
          if (id && !seenIds.has(id)) {
            uniqueInitiators.push({
              uuid: id,
              fullname: p.initiator?.fullname || p.initiator || "-",
            });
            seenIds.add(id);
          }
        });
        setInitiatorsList(uniqueInitiators);

      } catch (error) {
        toast.error(error.message);
        setProcurements([]);
      } finally {
        setIsLoading(false);
      }
    }
    fetch();
  }, [user]);

  function handleClick(event, uuid) {
    const url = `/procurement/${uuid}`;
    if (event.ctrlKey || event.metaKey || event.button === 1) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  }

  const filteredProcurements = selectedInitiator
    ? procurements.filter(p => (p.initiator?.uuid || p.initiator) === selectedInitiator)
    : procurements;

  const displayedProcurements = (
    showTerminated
      ? filteredProcurements
      : filteredProcurements.filter(p => TERMINATED_STEPS_NAME.every(stepName => p.step?.name !== stepName))
  ).slice().sort(sortProcurements);

  return (
    <Layout>
      <div className="flex flex-col mx-6 lg:mx-24 py-10 min-w-3.5 space-y-4">
        <h1 className="text-4xl font-semibold self-center text-center">Liste des marchés publics</h1>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          <div className="flex items-center gap-2">
            <Switch
              checked={showTerminated}
              onChange={e => setShowTerminated(e.target.checked)}
              color="primary"
              inputProps={{ 'aria-label': 'Afficher les marchés terminés' }}
            />
            <span className="text-gray-700 text-sm select-none">
              Afficher les marchés terminés
            </span>
            <FormControl size="small" sx={{ minWidth: 140, marginLeft: 7 }}>
              <InputLabel id="initiator-select-label">Initiateur</InputLabel>
              <Select
                labelId="initiator-select-label"
                id="initiator-select"
                value={selectedInitiator}
                label="Initiateur"
                onChange={e => setSelectedInitiator(e.target.value)}
              >
                <MenuItem value="">Tous</MenuItem>
                {initiatorsList.map(i =>
                  <MenuItem key={i.uuid} value={i.uuid}>{i.fullname}</MenuItem>,
                )}
              </Select>
            </FormControl>
          </div>
          {hasCreatePermission && (
            <div className="flex flex-row items-center gap-2 self-end md:self-auto">
              <Button
                onClick={() => navigate("/procurement")}
                variant="contained"
                disableElevation
              >
                <span>Créer un marché public</span>
              </Button>
              <Button
                onClick={() => navigate("/enterprises")}
                variant="contained"
                color="secondary"
                disableElevation
              >
                Liste des entreprises
              </Button>
              <Button
                onClick={() => navigate("/etapes-marche")}
                variant="contained"
                color="inherit"
                startIcon={<LightbulbIcon />}
                disableElevation
              >
                Les étapes
              </Button>
            </div>
          )}
        </div>
        <DataGrid
          rows={Array.isArray(displayedProcurements) ? displayedProcurements : []}
          columns={columns}
          columnVisibilityModel={columnVisibility}
          onColumnVisibilityModelChange={newModel => setColumnVisibility(newModel)}
          getRowClassName={params => params.row.isUserAction ? "bg-blue-50" : ""}
          loading={isLoading}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
            },
          }}
          paginationModel={paginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          onPaginationModelChange={model => setPaginationModel(model)}
          localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
        />
      </div>
    </Layout>
  );
}
